// Select com busca em tempo real - usado para estados/municípios no cadastro
// (listas grandes; para listas pequenas com opções fixas, usar Select)

import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { FaSearch, FaTimes } from 'react-icons/fa';
import { colors } from '../../../assets/styles/GlobalStyles';

const Container = styled.div`
  width: 100%;
  margin-bottom: 1.2rem;
  position: relative;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  color: ${colors.darkBlue};
  font-weight: 600;
  font-size: 0.9rem;
  span { color: #ff4444; }
`;

const InputContainer = styled.div`
  position: relative;
  width: 100%;
`;

const StyledInput = styled.input`
  width: 100%;
  padding: 0.9rem;
  padding-right: 2.5rem;
  border: 2px solid ${props => props.$hasError ? '#ff4444' : '#e8ecef'};
  border-radius: 8px;
  font-size: 0.95rem;
  transition: all 0.3s ease;
  box-sizing: border-box;
  background: ${props => props.disabled ? '#f5f5f5' : colors.backgroundLight};
  color: #333;

  // 16px evita o zoom automático do Safari/iOS ao focar o campo.
  @media (max-width: 768px) {
    font-size: 16px;
  }

  &:focus {
    outline: none;
    border-color: ${props => props.$hasError ? '#ff4444' : colors.lightBlue};
    background: white;
  }

  &::placeholder { color: #999; }
`;

const SearchIcon = styled(FaSearch)`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #999;
  font-size: 0.9rem;
  pointer-events: none;
`;

const ClearButton = styled.button`
  position: absolute;
  right: 6px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #999;
  cursor: pointer;
  padding: 4px;
  min-width: 32px;
  min-height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;

  &:hover { color: #ff4444; }
`;

const OptionsList = styled.ul`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  max-height: 250px;
  overflow-y: auto;
  background: white;
  border: 1px solid #e8ecef;
  border-radius: 8px;
  margin-top: 4px;
  padding: 0;
  list-style: none;
  z-index: 1000;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
`;

const OptionItem = styled.li`
  padding: 0.8rem 1rem;
  cursor: pointer;
  font-size: 0.95rem;
  border-bottom: 1px solid #f0f0f0;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${colors.lightBlue};
    color: white;
  }

  &:last-child { border-bottom: none; }
`;

const NoOptions = styled.div`
  padding: 1rem;
  text-align: center;
  color: #999;
  font-size: 0.9rem;
`;

const ErrorMessage = styled.small`
  color: #ff4444;
  font-size: 0.8rem;
  margin-top: 0.3rem;
  display: block;
`;

const SelectedValue = styled.div`
  padding: 0.9rem;
  border: 2px solid #e8ecef;
  border-radius: 8px;
  background: ${colors.backgroundLight};
  color: #333;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;

  &:hover { border-color: ${colors.lightBlue}; }
`;

const Placeholder = styled.span`
  color: #999;
`;

/**
 * @param {Function} getOptionLabel - Como exibir a opção (ex: estado.sigla)
 * @param {Function} getOptionValue - Qual valor usar (ex: estado.id)
 * @param {Function} [onSearch] - Se informado, ativa modo de busca remota: chamado com o
 *   termo digitado e deve retornar uma Promise<Array>. Substitui o filtro local em `options`.
 * @param {Function} [onSelectOption] - Chamado com a opção completa (ou null ao limpar) ao
 *   selecionar; útil para guardar um label junto do valor (ver `valueLabel`).
 * @param {string} [valueLabel] - Label já conhecido do valor atual, informado pelo componente
 *   pai. Tem prioridade sobre a busca em `options`/resultados remotos (necessário em modo
 *   assíncrono, já que o valor selecionado pode não estar entre os últimos resultados buscados).
 * @param {number} [minChars=1] - Mínimo de caracteres para disparar `onSearch`.
 */
export const SearchableSelect = ({
  label,
  name,
  options = [],
  value,
  valueLabel,
  onChange,
  onSelectOption,
  onSearch,
  minChars = 1,
  onBlur,
  error,
  required,
  disabled,
  placeholder = 'Digite para buscar...',
  getOptionLabel = (option) => option.nome || option.label || String(option),
  getOptionValue = (option) => option.id || option.value || option,
  noOptionsMessage = 'Nenhuma opção encontrada'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLabel, setSelectedLabel] = useState('');
  const [remoteOptions, setRemoteOptions] = useState([]);
  const [searching, setSearching] = useState(false);

  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const isSelectingRef = useRef(false); // Evita fechar o dropdown durante a seleção de uma opção
  const isAsync = typeof onSearch === 'function';

  // Atualiza label quando valor muda (ex: quando CEP preenche estado).
  // Se o pai já informa o label (obrigatório em modo assíncrono), usa direto -
  // evita depender de o valor selecionado estar entre os resultados buscados.
  useEffect(() => {
    if (valueLabel !== undefined) {
      setSelectedLabel(valueLabel || '');
      return;
    }

    const selected = options.find(opt =>
      String(getOptionValue(opt)) === String(value)
    );
    setSelectedLabel(selected ? getOptionLabel(selected) : '');
  }, [value, valueLabel, options, getOptionLabel, getOptionValue]);

  // Busca remota (debounce fica a cargo do `onSearch` informado pelo pai)
  useEffect(() => {
    if (!isAsync || !isOpen) return;

    if (searchTerm.trim().length < minChars) {
      setRemoteOptions([]);
      return;
    }

    let active = true;
    setSearching(true);

    onSearch(searchTerm).then(result => {
      if (active) setRemoteOptions(result || []);
    }).finally(() => {
      if (active) setSearching(false);
    });

    return () => { active = false; };
  }, [searchTerm, isAsync, isOpen, minChars, onSearch]);

  // Ao reabrir pra editar, começa a busca já com o rótulo atual só no modo local
  // (filtro em `options` já carregadas — refinar o texto existente funciona bem). No
  // modo remoto (`isAsync`), o rótulo de exibição costuma ter formatação extra (ex:
  // "CAMPO GRANDE - MS") que a API de busca não reconhece como termo de busca — abrir
  // já com isso digitado faz a busca não achar nada. Começa vazio, como uma busca nova.
  useEffect(() => {
    if (isOpen) setSearchTerm(isAsync ? '' : selectedLabel);
  }, [isOpen, selectedLabel, isAsync]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelectClick = () => {
    if (!disabled) {
      setIsOpen(true);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  };

  const handleOptionClick = (option) => {
    isSelectingRef.current = true;
    const optionValue = getOptionValue(option);

    onChange({
      target: { name: name, value: String(optionValue) }
    });
    onSelectOption?.(option);

    setIsOpen(false);
    isSelectingRef.current = false;
  };

  const handleClear = (e) => {
    e.stopPropagation();
    isSelectingRef.current = true;

    onChange({ target: { name: name, value: '' } });
    onSelectOption?.(null);
    setSearchTerm('');
    setIsOpen(false);
    isSelectingRef.current = false;
  };

  const handleBlur = () => {
    if (isSelectingRef.current) return; // Ignora se está selecionando uma opção

    setTimeout(() => {
      if (!containerRef.current?.contains(document.activeElement)) {
        setIsOpen(false);
      }
    }, 200);

    onBlur?.({ target: { name: name, value: value } });
  };

  const filteredOptions = isAsync
    ? remoteOptions
    : options.filter(option =>
        getOptionLabel(option).toLowerCase().includes(searchTerm.toLowerCase())
      );

  const belowMinChars = isAsync && searchTerm.trim().length < minChars;

  return (
    <Container ref={containerRef}>
      {label && (
        <Label>
          {label} {required && <span>*</span>}
        </Label>
      )}

      {isOpen ? (
        <InputContainer>
          <StyledInput
            ref={inputRef}
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onBlur={handleBlur}
            onKeyDown={(e) => {
              if (e.key === 'Escape') setIsOpen(false);
              if (e.key === 'Enter' && filteredOptions.length === 1) {
                handleOptionClick(filteredOptions[0]);
              }
            }}
            placeholder="Digite para buscar..."
            $hasError={!!error}
            disabled={disabled}
            autoComplete="off"
          />
          {searchTerm ? (
            <ClearButton onClick={() => setSearchTerm('')} type="button">
              <FaTimes size={14} />
            </ClearButton>
          ) : (
            <SearchIcon />
          )}
        </InputContainer>
      ) : (
        <SelectedValue
          onClick={handleSelectClick}
          $hasError={!!error}
          role="button"
          tabIndex={disabled ? -1 : 0}
        >
          {selectedLabel ? (
            <>
              <span>{selectedLabel}</span>
              {!disabled && (
                <ClearButton onClick={handleClear} type="button">
                  <FaTimes size={14} />
                </ClearButton>
              )}
            </>
          ) : (
            <Placeholder>{placeholder}</Placeholder>
          )}
          {!selectedLabel && <SearchIcon />}
        </SelectedValue>
      )}

      {isOpen && (
        <OptionsList>
          {belowMinChars ? (
            <NoOptions>Digite ao menos {minChars} caracteres para buscar</NoOptions>
          ) : searching ? (
            <NoOptions>Buscando...</NoOptions>
          ) : filteredOptions.length > 0 ? (
            filteredOptions.map((option, index) => {
              const optionValue = getOptionValue(option);
              const isSelected = String(optionValue) === String(value);

              return (
                <OptionItem
                  key={`${optionValue}-${index}`}
                  onClick={() => handleOptionClick(option)}
                  onMouseDown={(e) => e.preventDefault()} // Evita blur antes do click ser processado
                  style={isSelected ? {
                    backgroundColor: colors.lightBlue,
                    color: 'white'
                  } : {}}
                >
                  {getOptionLabel(option)}
                </OptionItem>
              );
            })
          ) : (
            <NoOptions>{noOptionsMessage}</NoOptions>
          )}
        </OptionsList>
      )}

      {error && <ErrorMessage>{error}</ErrorMessage>}
    </Container>
  );
};
